// API utility functions for ServeRS

const DEFAULT_API_BASE_URL = 'http://localhost:3000/api';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? DEFAULT_API_BASE_URL;
console.log('[API] Configured Base URL:', API_BASE_URL);

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

type ApiRequestOptions = Omit<RequestInit, 'body' | 'headers' | 'method'> & {
  method?: HttpMethod;
  auth?: boolean;
  body?: unknown;
  headers?: Record<string, string>;
};

interface ApiErrorPayload {
  message?: string;
  errors?: Record<string, string[]>;
  statusCode?: number;
}

let accessToken: string | null = null;

export const setAccessToken = (token: string | null) => {
  accessToken = token;
};

const buildQueryString = (params?: Record<string, unknown>) => {
  if (!params) return '';

  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    searchParams.append(key, String(value));
  });

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
};

const apiRequest = async <TResponse>(
  endpoint: string,
  options: ApiRequestOptions = {},
  queryParams?: Record<string, unknown>
): Promise<TResponse> => {
  const { auth, headers, method = 'GET', body, ...rest } = options;

  const url = `${API_BASE_URL}${endpoint}${buildQueryString(queryParams)}`;

  const requestInit: RequestInit = {
    method,
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      ...(auth && accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...headers,
    },
  };

  if (body !== undefined && body !== null) {
    requestInit.body = typeof body === 'string' ? body : JSON.stringify(body);
  }

  console.log(`[API] ${method} ${url}`, { auth, hasToken: !!accessToken, body: body ? JSON.stringify(body).substring(0, 200) : null });

  const response = await fetch(url, requestInit);

  console.log(`[API] Response: ${response.status} ${response.statusText}`);

  if (!response.ok) {
    let errorPayload: ApiErrorPayload | undefined;
    try {
      errorPayload = await response.json();
    } catch (error) {
      errorPayload = { message: response.statusText };
    }

    console.error(`[API] Error:`, errorPayload);

    const error = new Error(errorPayload?.message || 'Request failed');
    (error as Error & { status?: number; details?: ApiErrorPayload }).status = response.status;
    (error as Error & { status?: number; details?: ApiErrorPayload }).details = errorPayload;
    throw error;
  }

  if (response.status === 204) {
    return undefined as TResponse;
  }

  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    const data = await response.json();
    console.log(`[API] Success:`, JSON.stringify(data).substring(0, 200));
    return data as TResponse;
  }

  return (await response.text()) as TResponse;
};

// Authentication API calls
type TokensResponse = {
  accessToken: string;
  refreshToken: string;
  expiresIn: string;
  tokenType: 'Bearer';
};

export interface AuthResponse {
  user: {
    id: string;
    email: string;
    username: string;
    displayName?: string;
    avatarUrl?: string;
    bio?: string;
    location?: string;
    websiteUrl?: string;
    role: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
  };
  tokens: TokensResponse;
}

export const authAPI = {
  login: async (email: string, password: string) =>
    apiRequest<AuthResponse>('/auth/login', {
      method: 'POST',
      body: { email, password },
    }),

  register: async (params: { email: string; password: string; username: string; displayName?: string }) =>
    apiRequest<AuthResponse>('/auth/register', {
      method: 'POST',
      body: params,
    }),

  refresh: async (refreshToken: string) =>
    apiRequest<AuthResponse>('/auth/refresh', {
      method: 'POST',
      body: { refreshToken },
    }),

  logout: async (refreshToken: string) =>
    apiRequest<{ success: boolean }>('/auth/logout', {
      method: 'POST',
      body: { refreshToken },
      auth: true,
    }),

  me: async () =>
    apiRequest<{ user: AuthResponse['user'] }>('/auth/me', {
      method: 'GET',
      auth: true,
    }),
};

// Server API calls
export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ServerRecord {
  id: string;
  name: string;
  slug: string;
  description: string;
  shortDescription?: string;
  featuredUntil?: string;
  game?: string;
  region?: string;
  ipAddress?: string;
  websiteUrl?: string;
  downloadUrl?: string;
  bannerUrl?: string;
  discordLink?: string;
  tags?: string[];
  currentPlayers?: number;
  maxPlayers?: number;
  screenshots?: {
    id: string;
    url: string;
    caption?: string;
    sortOrder: number;
  }[];
  ratingAverage: number;
  ratingCount: number;
  helpfulCount: number;
  notHelpfulCount: number;
  helpfulUserIds: string[];
  notHelpfulUserIds: string[];
  userId: string;
  serverId: string;
  createdAt: string;
  updatedAt: string;
  status: 'online' | 'offline' | 'maintenance' | 'starting_soon';
  bossContent?: {
    id: string;
    url: string;
    caption?: string;
    sortOrder: number;
  }[];
  bossContentTitle?: string;
  itemTransformations?: {
    id: string;
    url: string;
    caption?: string;
    sortOrder: number;
  }[];
  itemContentTitle?: string;
  partners?: {
    name: string;
    url: string;
    iconUrl: string;
  }[];
  onlineRating?: number;
  managementRating?: number;
  gameplayRating?: number;
  user?: {
    id: string;
    email: string;
    username: string;
    displayName?: string;
    avatarUrl?: string;
    role: string;
    createdAt: string;
    updatedAt: string;
  };
}

export interface CreateServerPayload {
  name: string;
  description: string;
  shortDescription?: string;
  game?: string;
  region?: string;
  ipAddress?: string;
  websiteUrl?: string;
  downloadUrl?: string;
  bannerUrl?: string;
  discordLink?: string;
  tags?: string[];
  status?: 'online' | 'offline' | 'maintenance' | 'starting_soon';
  currentPlayers?: number;
  maxPlayers?: number;
  screenshots?: {
    id: string;
    url: string;
    caption?: string;
    sortOrder: number;
  }[];
  bossContent?: {
    id: string;
    url: string;
    caption?: string;
    sortOrder: number;
  }[];
  bossContentTitle?: string;
  itemTransformations?: {
    id: string;
    url: string;
    caption?: string;
    sortOrder: number;
  }[];
  itemContentTitle?: string;
  partners?: {
    name: string;
    url: string;
    iconUrl: string;
  }[];
  onlineRating?: number;
  managementRating?: number;
  gameplayRating?: number;
}

export interface UpdateServerPayload {
  name?: string;
  description?: string;
  shortDescription?: string;
  game?: string;
  region?: string;
  ipAddress?: string;
  websiteUrl?: string;
  downloadUrl?: string;
  bannerUrl?: string;
  discordLink?: string;
  tags?: string[];
  status?: 'online' | 'offline' | 'maintenance' | 'starting_soon';
  currentPlayers?: number;
  maxPlayers?: number;
  screenshots?: {
    id: string;
    url: string;
    caption?: string;
    sortOrder: number;
  }[];
  bossContent?: {
    id: string;
    url: string;
    caption?: string;
    sortOrder: number;
  }[];
  bossContentTitle?: string;
  itemTransformations?: {
    id: string;
    url: string;
    caption?: string;
    sortOrder: number;
  }[];
  itemContentTitle?: string;
  partners?: {
    name: string;
    url: string;
    iconUrl: string;
  }[];
  onlineRating?: number;
  managementRating?: number;
  gameplayRating?: number;
}

export const serverAPI = {
  getAllServers: async (params?: {
    search?: string;
    tag?: string;
    status?: string;
    page?: number;
    limit?: number;
  }) => apiRequest<PaginatedResponse<ServerRecord>>('/servers', { method: 'GET' }, params),

  getServerById: async (id: string) =>
    apiRequest<ServerRecord>(`/servers/${id}`),

  createServer: async (serverData: CreateServerPayload) =>
    apiRequest<ServerRecord>('/servers', {
      method: 'POST',
      body: serverData,
      auth: true,
    }),

  addServer: async (serverData: Partial<ServerRecord>) =>
    apiRequest<ServerRecord>('/servers', {
      method: 'POST',
      body: serverData,
      auth: true,
    }),

  updateServer: async (id: string, serverData: UpdateServerPayload) =>
    apiRequest<ServerRecord>(`/servers/${id}`, {
      method: 'PUT',
      body: serverData,
      auth: true,
    }),

  deleteServer: async (id: string) =>
    apiRequest<void>(`/servers/${id}`, {
      method: 'DELETE',
      auth: true,
    }),

  visitServer: async (id: string) =>
    apiRequest<{ message?: string; redirected?: boolean }>(`/servers/${id}/visit`, {
      method: 'GET',
    }),

  downloadServerClient: async (id: string) =>
    apiRequest<{ message?: string; redirected?: boolean }>(`/servers/${id}/download`, {
      method: 'GET',
    }),

  getHomeStats: async () =>
    apiRequest<{ totalServers: number; newServers: number; comingSoon: number }>('/servers/stats/home', {
      method: 'GET',
    }),

  getFavorites: async (params?: { page?: number; limit?: number }) =>
    apiRequest<PaginatedResponse<ServerRecord>>('/servers/favorites', { method: 'GET', auth: true }, params),

  toggleFavorite: async (id: string) =>
    apiRequest<{ favorited: boolean }>(`/servers/${id}/favorite`, {
      method: 'POST',
      auth: true,
    }),

  // Rating endpoints
  rateServer: async (id: string, rating: number) =>
    apiRequest<{ id: string; serverId: string; userId: string; rating: number; createdAt: string; updatedAt: string }>(`/servers/${id}/rate`, {
      method: 'POST',
      body: { rating },
      auth: true,
    }),

  getMyRating: async (id: string) =>
    apiRequest<{ rating: number | null }>(`/servers/${id}/my-rating`, {
      method: 'GET',
      auth: true,
    }),

  getRatingStats: async (id: string) =>
    apiRequest<{ averageRating: number; totalRatings: number }>(`/servers/${id}/rating-stats`, {
      method: 'GET',
    }),
};

export interface CommentRecord {
  id: string;
  content: string;
  rating: number;
  helpfulCount: number;
  notHelpfulCount: number;
  helpfulUserIds: string[];
  notHelpfulUserIds: string[];
  userId: string;
  serverId: string;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    email: string;
    username: string;
    displayName?: string;
    avatarUrl?: string;
    role: string;
    createdAt: string;
    updatedAt: string;
  };
}

export const commentAPI = {
  getForServer: async (serverId: string, params?: { page?: number; limit?: number }) =>
    apiRequest<PaginatedResponse<CommentRecord>>(`/comments/${serverId}`, { method: 'GET' }, params),

  createComment: async (payload: { serverId: string; content: string; rating: number }) =>
    apiRequest<CommentRecord>('/comments', {
      method: 'POST',
      body: payload,
      auth: true,
    }),

  deleteComment: async (commentId: string) =>
    apiRequest<void>(`/comments/${commentId}`, {
      method: 'DELETE',
      auth: true,
    }),

  voteOnComment: async (commentId: string, vote: 'helpful' | 'not_helpful') =>
    apiRequest<CommentRecord>(`/comments/${commentId}/vote`, {
      method: 'PUT',
      body: { vote },
      auth: true,
    }),
};

// Forum API calls
export const forumAPI = {
  getThreads: async (serverId?: string) =>
    apiRequest('/forum/threads', { method: 'GET' }, serverId ? { server: serverId } : undefined),

  createThread: async (threadData: unknown) =>
    apiRequest('/forum/threads', {
      method: 'POST',
      body: threadData,
      auth: true,
    }),

  getPosts: async (threadId: string) =>
    apiRequest(`/forum/threads/${threadId}/posts`),

  createPost: async (threadId: string, postData: unknown) =>
    apiRequest(`/forum/threads/${threadId}/posts`, {
      method: 'POST',
      body: postData,
      auth: true,
    }),
};

// User API calls
export const userAPI = {
  getProfile: async () =>
    apiRequest<{
      user: {
        id: string;
        email: string;
        username: string;
        displayName?: string;
        avatarUrl?: string;
        bio?: string;
        location?: string;
        websiteUrl?: string;
        role: string;
        isActive: boolean;
        createdAt: string;
        updatedAt: string;
      };
      stats: {
        totalServers: number;
        activeServers: number;
        averageServerRating: number;
        totalComments: number;
        memberSince: string;
      };
    }>('/users/me', {
      method: 'GET',
      auth: true,
    }),

  updateProfile: async (profileData: unknown) =>
    apiRequest<{
      user: {
        id: string;
        email: string;
        username: string;
        displayName?: string;
        avatarUrl?: string;
        bio?: string;
        location?: string;
        websiteUrl?: string;
        role: string;
        isActive: boolean;
        createdAt: string;
        updatedAt: string;
      };
      stats: {
        totalServers: number;
        activeServers: number;
        averageServerRating: number;
        totalComments: number;
        memberSince: string;
      };
    }>('/users/me', {
      method: 'PUT',
      body: profileData,
      auth: true,
    }),

  getPublicProfile: async (identifier: string) =>
    apiRequest<{
      user: {
        id: string;
        username: string;
        displayName?: string;
        avatarUrl?: string;
        bio?: string;
        location?: string;
        websiteUrl?: string;
        createdAt: string;
      };
      stats: {
        totalServers: number;
        activeServers: number;
        averageServerRating: number;
        totalComments: number;
        memberSince: string;
      };
    }>(`/users/${encodeURIComponent(identifier)}/profile`, {
      method: 'GET',
    }),

  completeRegistration: async (username: string) =>
    apiRequest<{
      user: {
        id: string;
        email: string;
        username: string;
        displayName?: string;
        avatarUrl?: string;
        bio?: string;
        location?: string;
        websiteUrl?: string;
        role: string;
        isActive: boolean;
        createdAt: string;
        updatedAt: string;
      };
      stats: {
        totalServers: number;
        activeServers: number;
        averageServerRating: number;
        totalComments: number;
        memberSince: string;
      };
    }>('/users/complete-registration', {
      method: 'POST',
      body: { username },
      auth: true,
    }),
};

// Admin API calls
export const adminAPI = {
  getUsers: async (params?: { page?: number; limit?: number; search?: string }) =>
    apiRequest<PaginatedResponse<AuthResponse['user']>>('/admin/users', { method: 'GET', auth: true }, params),

  getComments: async (params?: { page?: number; limit?: number; search?: string }) =>
    apiRequest<PaginatedResponse<CommentRecord>>('/admin/comments', { method: 'GET', auth: true }, params),

  getStats: async () =>
    apiRequest<{ totalUsers: number; totalServers: number; totalComments: number }>('/admin/dashboard/stats', {
      method: 'GET',
      auth: true,
    }),

  updateUserRole: async (userId: string, role: string) =>
    apiRequest<{ id: string; role: string }>(`/admin/users/${userId}/role`, {
      method: 'PATCH',
      body: { role },
      auth: true,
    }),

  updateUserStatus: async (userId: string, isActive: boolean) =>
    apiRequest<{ id: string; isActive: boolean }>(`/admin/users/${userId}/status`, {
      method: 'PATCH',
      body: { isActive },
      auth: true,
    }),

  setFeatured: async (serverId: string, durationDays: number) =>
    apiRequest<void>(`/admin/servers/${serverId}/featured`, {
      method: 'POST',
      body: { durationDays },
      auth: true,
    }),

  removeFeatured: async (serverId: string) =>
    apiRequest<void>(`/admin/servers/${serverId}/featured`, {
      method: 'DELETE',
      auth: true,
    }),

  deleteComment: async (commentId: string) =>
    apiRequest<void>(`/admin/comments/${commentId}`, {
      method: 'DELETE',
      auth: true,
    }),

  getLogs: async (params?: { page?: number; limit?: number }) =>
    apiRequest<PaginatedResponse<{ id: string; action: string; user: string; ip: string; time: string; status: string }>>('/admin/logs', {
      method: 'GET',
      auth: true,
    }, params),
};

// Ad API calls
export interface AdRecord {
  id: string;
  title: string;
  description?: string;
  imageUrl: string;
  linkUrl?: string;
  placement: 'startup' | 'home_banner' | 'sidebar';
  startDate: string;
  endDate: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAdPayload {
  title: string;
  description?: string;
  imageUrl: string;
  linkUrl?: string;
  placement: 'startup' | 'home_banner' | 'sidebar';
  startDate: string;
  endDate: string;
  isActive: boolean;
}

export const adsAPI = {
  getAll: async () =>
    apiRequest<AdRecord[]>('/ads', { method: 'GET', auth: true }),

  getActive: async (placement?: string) =>
    apiRequest<AdRecord[]>('/ads/active', { method: 'GET' }, placement ? { placement } : undefined),

  create: async (payload: CreateAdPayload) =>
    apiRequest<AdRecord>('/ads', {
      method: 'POST',
      body: payload,
      auth: true,
    }),

  update: async (id: string, payload: Partial<CreateAdPayload>) =>
    apiRequest<AdRecord>(`/ads/${id}`, {
      method: 'PUT',
      body: payload,
      auth: true,
    }),

  delete: async (id: string) =>
    apiRequest<void>(`/ads/${id}`, {
      method: 'DELETE',
      auth: true,
    }),
};

// Tags API calls
export interface TagRecord {
  id: string;
  name: string;
  usageCount: number;
}

export const tagsAPI = {
  getPopular: async (limit?: number) =>
    apiRequest<{ success: boolean; data: TagRecord[] }>('/tags/popular', { method: 'GET' }, limit ? { limit } : undefined),

  search: async (query: string, limit?: number) =>
    apiRequest<{ success: boolean; data: TagRecord[] }>('/tags/search', { method: 'GET' }, { q: query, limit }),

  addTag: async (name: string) =>
    apiRequest<{ success: boolean; data: TagRecord }>('/tags', {
      method: 'POST',
      body: { name },
      auth: true,
    }),

  addTags: async (tags: string[]) =>
    apiRequest<{ success: boolean; message: string }>('/tags/batch', {
      method: 'POST',
      body: { tags },
      auth: true,
    }),
};


export default {
  authAPI,
  serverAPI,
  commentAPI,
  forumAPI,
  userAPI,
  adminAPI,
  adsAPI,
  tagsAPI,
  setAccessToken,
};